import React, { useCallback, useEffect, useRef } from "react";
import DataGrid, {
  ColumnChooser,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import "../../../styles/masters.css";

const PAGE_SIZES = [10, 20, 50, 100];

const faClass = (icon) => (!icon ? "" : /^fa[srlbd]?\s|^fa-/.test(icon) ? icon : `fas fa-${icon}`);

/** Toolbar button in the site's primary style (plain DOM: the toolbar renders it outside React). */
export function primaryToolbarButton(item, onClick) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn btn-primary mst-toolbar-primary";
  button.title = item.hint ?? item.text ?? "";
  if (item.icon) {
    const icon = document.createElement("i");
    icon.className = faClass(item.icon);
    icon.setAttribute("aria-hidden", "true");
    button.appendChild(icon);
  }
  if (item.text) button.appendChild(document.createTextNode(`${item.icon ? " " : ""}${item.text}`));
  button.addEventListener("click", onClick);
  return button;
}

/**
 * Standard DataGrid for Masters screens.
 *
 * Client mode: `dataSource` is an array; paging/sort/search happen in the browser.
 * Remote mode: `remote` + a store from createPagedStore(); the API pages and sorts.
 *
 * toolbarItems: [{ text, icon, hint, onClick, primary }] rendered right-aligned before Refresh.
 *   primary: true draws a Bootstrap "btn btn-primary" (the site's Add button look, e.g. Hotels / Staff Address);
 *   icon is then a Font Awesome class or a short name ("plus" -> "fas fa-plus").
 * onRefresh: async reload for client mode (remote mode refreshes the store itself).
 * allowColumnChooser: false hides the toolbar column-chooser button.
 */
export default function MasterGrid({
  dataSource,
  remote = false,
  loading = false,
  keyExpr,
  exportFileName,
  toolbarItems = [],
  onRefresh,
  pageSize = 20,
  allowColumnChooser = true,
  gridRef,
  children,
  ...gridProps
}) {
  const innerRef = useRef(null);
  const ref = gridRef ?? innerRef;

  // onToolbarPreparing is captured by DevExtreme; read the latest props through a ref.
  const latest = useRef({ toolbarItems, onRefresh, remote });
  latest.current = { toolbarItems, onRefresh, remote };

  useEffect(() => {
    const grid = ref.current?.instance;
    if (!grid || remote) return;
    if (loading) grid.beginCustomLoading();
    else grid.endCustomLoading();
  }, [loading, remote, ref]);

  const handleToolbarPreparing = useCallback(
    (e) => {
      const { toolbarItems: items } = latest.current;
      const actionItems = items.map((item, index) => {
        const onClick = () => latest.current.toolbarItems[index]?.onClick();
        if (item.primary) {
          return { location: "after", template: () => primaryToolbarButton(item, onClick) };
        }
        return {
          location: "after",
          widget: "dxButton",
          options: {
            text: item.text,
            icon: item.icon,
            hint: item.hint ?? item.text,
            type: item.text ? "default" : "normal",
            onClick,
          },
        };
      });
      const refreshItem = {
        location: "after",
        widget: "dxButton",
        options: {
          icon: "refresh",
          hint: "Refresh",
          onClick: async (args) => {
            args.component.option("icon", "fas fa-spinner fa-spin");
            try {
              if (latest.current.remote || !latest.current.onRefresh) {
                await ref.current?.instance.refresh();
              } else {
                await latest.current.onRefresh();
              }
            } finally {
              args.component.option("icon", "refresh");
            }
          },
        },
      };
      e.toolbarOptions.items.unshift(...actionItems, refreshItem);
    },
    [ref],
  );

  return (
    <div className="card mst-grid">
      <div className="card-body">
        <DataGrid
          ref={ref}
          dataSource={dataSource}
          keyExpr={remote ? undefined : keyExpr}
          remoteOperations={remote ? { paging: true, sorting: true, filtering: true } : false}
          showBorders
          showRowLines
          showColumnLines
          columnAutoWidth
          allowColumnResizing
          columnResizingMode="widget"
          hoverStateEnabled
          noDataText={loading ? "Loading..." : "No record found."}
          onToolbarPreparing={handleToolbarPreparing}
          {...gridProps}
        >
          {children}
          <HeaderFilter visible={!remote} allowSearch />
          <SearchPanel visible width={240} placeholder="Search..." />
          {allowColumnChooser && <ColumnChooser enabled mode="select" />}
          <Paging defaultPageSize={pageSize} />
          <Pager showPageSizeSelector allowedPageSizes={PAGE_SIZES} showInfo visible />
          {exportFileName && (
            <Export enabled allowExportSelectedData={false} fileName={exportFileName} />
          )}
        </DataGrid>
      </div>
    </div>
  );
}
